import React, { useEffect, useState } from 'react';
import { Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import {
    HomeOutlined,
    TeamOutlined,
    RocketOutlined,
    HeartOutlined,
    SafetyCertificateOutlined,
    GlobalOutlined,
    CustomerServiceOutlined,
    TrophyOutlined
} from '@ant-design/icons';
import { useScrollAnimation } from '../../common/hooks';
import Logo from '../../assets/logo-tigoon.png';
import './AboutPage.css';

const AboutPage = () => {
    const [pageLoaded, setPageLoaded] = useState(false);

    // Animation hooks
    const [introRef, introVisible] = useScrollAnimation({ threshold: 0.1 });
    const [missionRef, missionVisible] = useScrollAnimation({ threshold: 0.1 });
    const [valuesRef, valuesVisible] = useScrollAnimation({ threshold: 0.1 });
    const [teamRef, teamVisible] = useScrollAnimation({ threshold: 0.1 });

    useEffect(() => {
        setTimeout(() => setPageLoaded(true), 100);
    }, []);

    const values = [
        {
            icon: <HeartOutlined />,
            title: 'Passion',
            description: 'Nous sommes passionnés par l\'amélioration du quotidien des chauffeurs, des livreurs et des commerçants.'
        },
        {
            icon: <SafetyCertificateOutlined />,
            title: 'Fiabilité',
            description: 'Une plateforme sécurisée et fiable pour gérer vos activités en toute sérénité.'
        },
        {
            icon: <TeamOutlined />,
            title: 'Communauté',
            description: 'Nous construisons une communauté solidaire de partenaires : chauffeurs, livreurs et commerçants.'
        },
        {
            icon: <RocketOutlined />,
            title: 'Innovation',
            description: 'Nous innovons constamment pour vous offrir les meilleurs outils.'
        }
    ];

    const stats = [
        { value: '10 000+', label: 'Chauffeurs actifs' },
        { value: '500 000+', label: 'Courses effectuées' },
        { value: '24/7', label: 'Support disponible' },
        { value: '4.8/5', label: 'Note moyenne' }
    ];

    return (
        <div className="about-page">
            {/* Hero Section - Simple Banner */}
            <section className={`page-banner ${pageLoaded ? 'banner-loaded' : ''}`}>
                <div className="banner-bg-pattern"></div>
                <div className="container">
                    <h1 className={`banner-title ${pageLoaded ? 'animate-in' : ''}`}>
                        À Propos
                    </h1>
                    <p className={`banner-description ${pageLoaded ? 'animate-in' : ''}`}>
                        Découvrez l'histoire et la mission de TIGOON Connect
                    </p>
                    <nav className={`breadcrumb ${pageLoaded ? 'animate-in' : ''}`}>
                        <Link to="/"><HomeOutlined /> Accueil</Link>
                        <span className="separator">/</span>
                        <span className="current">À propos</span>
                    </nav>
                </div>
            </section>

            {/* About Section */}
            <section
                ref={introRef}
                className={`about-intro-section ${introVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <Row gutter={[48, 48]} align="middle">
                        <Col xs={24} md={12}>
                            <div className={`about-image-wrapper ${introVisible ? 'animate-fade-right is-visible' : 'animate-fade-right'}`}>
                                <img src={Logo} alt="TIGOON Connect" className="about-logo" />
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className={`about-content ${introVisible ? 'animate-fade-left is-visible' : 'animate-fade-left'}`}>
                                <span className="section-badge">Notre histoire</span>
                                <h2>Notre <span className="text-gold">Histoire</span></h2>
                                <p>
                                    TIGOON Connect est née de la volonté d'accompagner les chauffeurs VTC
                                    dans leur quotidien. Nous avons créé une application qui simplifie
                                    la gestion de leurs activités tout en leur offrant des avantages
                                    exclusifs.
                                </p>
                                <p>
                                    Depuis, la plateforme s'est ouverte à d'autres métiers : les livreurs
                                    à moto, les commerçants qui vendent et se font livrer, et les services
                                    d'assistance et de dépannage.
                                </p>
                                <p>
                                    Basée à Abidjan, en Côte d'Ivoire, notre équipe travaille chaque jour
                                    pour améliorer l'expérience de tous nos partenaires et leur
                                    permettre de maximiser leurs revenus.
                                </p>
                            </div>
                        </Col>
                    </Row>
                </div>
            </section>

            {/* Mission Section */}
            <section
                ref={missionRef}
                className={`mission-section ${missionVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <Row gutter={[48, 48]} align="middle">
                        <Col xs={24} md={12}>
                            <div className={`mission-content ${missionVisible ? 'animate-fade-right is-visible' : 'animate-fade-right'}`}>
                                <span className="section-badge">Notre mission</span>
                                <h2>Notre <span className="text-gold">Mission</span></h2>
                                <p>
                                    Notre mission est simple : devenir le partenaire de confiance des
                                    chauffeurs, des livreurs et des commerçants en leur offrant les outils
                                    et les avantages dont ils ont besoin pour réussir.
                                </p>
                                <ul className="mission-list">
                                    <li>
                                        <GlobalOutlined />
                                        <span>Étendre notre présence en Afrique de l'Ouest</span>
                                    </li>
                                    <li>
                                        <CustomerServiceOutlined />
                                        <span>Offrir un support de qualité 24/7</span>
                                    </li>
                                    <li>
                                        <TrophyOutlined />
                                        <span>Récompenser la fidélité de nos partenaires</span>
                                    </li>
                                </ul>
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className={`stats-wrapper ${missionVisible ? 'animate-fade-left is-visible' : 'animate-fade-left'}`}>
                                <Row gutter={[16, 16]}>
                                    {stats.map((stat, index) => (
                                        <Col xs={12} key={index}>
                                            <div
                                                className="stat-box"
                                                style={{ transitionDelay: `${index * 0.1}s` }}
                                            >
                                                <span className="stat-value">{stat.value}</span>
                                                <span className="stat-label">{stat.label}</span>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </div>
            </section>

            {/* Values Section */}
            <section
                ref={valuesRef}
                className={`values-section ${valuesVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`section-header ${valuesVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}>
                        <span className="section-badge">Nos principes</span>
                        <h2>Nos <span className="text-gold">Valeurs</span></h2>
                        <p>Les principes qui guident notre action au quotidien</p>
                    </div>
                    <Row gutter={[24, 24]}>
                        {values.map((value, index) => (
                            <Col xs={24} sm={12} md={6} key={index}>
                                <div
                                    className={`value-card ${valuesVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                                    style={{ transitionDelay: `${index * 0.1}s` }}
                                >
                                    <div className="value-icon">{value.icon}</div>
                                    <h3>{value.title}</h3>
                                    <p>{value.description}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </section>

            {/* Team Section */}
            <section
                ref={teamRef}
                className={`team-section ${teamVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`team-content ${teamVisible ? 'animate-scale is-visible' : 'animate-scale'}`}>
                        <span className="section-badge-light">Notre équipe</span>
                        <h2>Une équipe <span className="text-gold">dédiée</span></h2>
                        <p>
                            Notre équipe est composée de professionnels passionnés par la technologie
                            et le transport. Nous travaillons ensemble pour vous offrir la meilleure
                            expérience possible.
                        </p>
                        <div className="team-cta">
                            <Link to="/contact" className="contact-btn">
                                Nous contacter
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;
